import { createTheme, styled, type Theme } from '@mui/material/styles'
import { Box } from '@mui/material'

// ── Brand core ────────────────────────────────────────────────────────────────
export const COLOR_PRIMARY = '#f4b632'  // Gold  - Core / alerts
export const COLOR_SECONDARY = '#b93b46'  // Red   - Tender Ninja
export const COLOR_TERTIARY = '#469127'
// Green - success / check marks
export const COLOR_QUATERNARY = '#176bd7' // Blue  - Foresight / analytics
//export const COLOR_SECONDARY = '#3BB963'  // Red   - Tender Ninja

// ── Dark navy scale (hero & page backgrounds) ─────────────────────────────────
export const NAVBAR_BG = '#0d1b2a'  // Darkest - navbar & footer bg
export const DARK_900 = '#0f2035'  // Stats section bg
export const DARK_800 = '#0f2a42'  // Hero gradient end
export const DARK_700 = '#1a3050'  // Hero gradient mid
export const DARK_600 = '#1a2e40'  // Core product page accent
export const HOME_HERO_GRADIENT = `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_700} 60%, ${DARK_800} 100%)`
export const HOME_HERO_PANEL_BG = 'rgba(255,255,255,0.04)'  // Live tenders widget wash over the hero

// ── Product page hero backgrounds ─────────────────────────────────────────────
export const FORESIGHT_HERO_START = '#061428'
export const FORESIGHT_HERO_MID = '#0a2040'
export const FORESIGHT_HERO_END = '#061830'
export const NINJA_HERO_START = '#1a0810'
export const NINJA_HERO_MID = '#2a0f18'
export const NINJA_HERO_END = '#1a0c22'
export const BID_HERO_START = '#071a0a'
export const BID_HERO_MID = '#0d2612'
export const BID_HERO_END = '#081a10'
export const SOLUTIONS_HERO_START = '#04161e'
export const SOLUTIONS_HERO_MID = '#082230'
export const SOLUTIONS_HERO_END = '#051c28'
export const EXPAND_HERO_START = '#071a08'  // Dark green  Expanding Reach pages
export const EXPAND_HERO_MID = '#0d2610'
export const EXPAND_HERO_END = '#091c0a'
export const EXECUTION_HERO_START = '#06101e'  // Dark blue  Execution Support pages
export const EXECUTION_HERO_MID = '#0a1a32'
export const EXECUTION_HERO_END = '#071428'

// ── Hover / active variants ───────────────────────────────────────────────────
//export const COLOR_SOLUTIONS = '#0891b2'     // Teal  - Solutions pages
export const COLOR_SOLUTIONS = COLOR_PRIMARY
export const COLOR_SOLUTIONS_HOVER = '#0778a0'
export const COLOR_PRIMARY_HOVER = '#e5a820'  // Gold hover
export const COLOR_SECONDARY_HOVER = '#a0313c'  // Red hover
export const COLOR_SECONDARY_DARK = '#9e2e38'  // Red dark hover
export const COLOR_TERTIARY_HOVER = '#3a7a1f'  // Green hover
export const COLOR_QUATERNARY_HOVER = '#1258b5'  // Blue hover

// ── Utility ───────────────────────────────────────────────────────────────────
export const BG_LIGHT = '#f7f8fa'  // Light section background
export const COLOR_LIVE = '#6ddb4a'  // Live indicator green dot
export const COLOR_AMBER = '#c8890a'  // Deadline / warning amber
export const COLOR_SKY = '#4A90E2'  // Navbar solution group accent

export const BORDER_BLACK = '1px solid black';
export const BORDER_WHITE = '1px solid white';
export const CHECKCIRCLE_SIZE = 18;
export const SECTION_DARK = { backgroundColor: DARK_800, py: { xs: 4, md: 12 } } as const
export const SECTION_LIGHT = { backgroundColor: BG_LIGHT, py: { xs: 4, md: 8 } } as const

export const COLOR_GREY100 = 'rgb(0, 0, 0, 0.1)'
export const COLOR_GREY200 = 'rgb(0, 0, 0, 0.2)'
export const COLOR_GREY300 = 'rgb(0, 0, 0, 0.3)'
export const COLOR_GREY400 = 'rgb(0, 0, 0, 0.4)'
export const COLOR_GREY500 = 'rgb(0, 0, 0, 0.5)'
export const COLOR_GREY600 = 'rgb(0, 0, 0, 0.6)'
export const COLOR_GREY700 = 'rgb(0, 0, 0, 0.7)'
export const COLOR_GREY800 = 'rgb(0, 0, 0, 0.8)'
export const COLOR_GREY900 = 'rgb(0, 0, 0, 0.9)'


export const themeBorderColor = (theme: Theme) =>
    theme.palette.mode === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.grey[300]

export const themeBorder = (theme: Theme) => `1px solid ${themeBorderColor(theme)}`

export const RoundedBox = styled(Box)(({ theme }) => ({
    border: themeBorder(theme),
    borderRadius: theme.shape.borderRadius,
}))

declare module '@mui/material/styles' {
    interface Palette {
        tertiary: Palette['primary']
        quaternary: Palette['primary']
    }
    interface PaletteOptions {
        tertiary?: PaletteOptions['primary']
        quaternary?: PaletteOptions['primary']
    }
}

export const theme = createTheme({
    palette: {
        primary: { main: COLOR_PRIMARY },
        secondary: { main: COLOR_SECONDARY },
        tertiary: { main: COLOR_TERTIARY },
        quaternary: { main: COLOR_QUATERNARY },
        background: {
            default: BG_LIGHT,
            paper: '#ffffff',
        },
        text: {
            primary: '#1a2a3a',
            secondary: '#4a5568',
        },
        action: { hover: `${COLOR_PRIMARY}18` },
    },
    breakpoints: {
        values: { xs: 0, sm: 640, md: 768, lg: 1024, xl: 1440 },
    },
    typography: {
        fontFamily: `'Inter', Arial, Helvetica, sans-serif`,
        h1: { fontSize: '56px', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.5px' },
        h2: { fontSize: '40px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.5px' },
        h3: { fontSize: '28px', fontWeight: 700, lineHeight: 1.3 },
        h4: { fontSize: '22px', fontWeight: 600 },
        h5: { fontSize: '18px', fontWeight: 600 },
        h6: { fontSize: '16px', fontWeight: 600 },
        body1: { fontSize: '16px', lineHeight: 1.7 },
        body2: { fontSize: '14px', lineHeight: 1.6 },
        caption: { fontSize: '13px', lineHeight: 1.4 },
        button: { fontSize: '15px', fontWeight: 600, textTransform: 'none' as const },
    },
    shape: { borderRadius: 8 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: '10px 24px',
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                },
                sizeLarge: {
                    //padding: '13px 32px',
                    //padding: '13px 32px',
                    fontSize: '16px',
                },
                containedPrimary: {
                    color: NAVBAR_BG,
                    '&:hover': { backgroundColor: COLOR_PRIMARY_HOVER },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                    border: '1px solid rgba(0,0,0,0.06)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: NAVBAR_BG, // = DARK_900
                    boxShadow: '0 1px 0 rgba(255,255,255,0.06)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontWeight: 500 },
            },
        },
    },
})
